import { request } from '@/lib/api'

/**
 * Первичные документы и договоры (тикет #73, ТЗ §4.3).
 *
 * ## Почему отдельно от `card/api.ts`
 *
 * `card/api.ts` — универсальный слой каркаса #66: он читает и пишет **одну плоскую строку**
 * через механизм списков. Первичный документ плоской строкой не описывается: у него строки
 * с построчным НДС и подпись, удостоверяющая содержимое. Показать `total` без разбора
 * значит показать бухгалтеру цифру, которую он не может проверить, — та же причина, по
 * которой у счёта (#72) есть свой слой.
 *
 * ## Про хеш подписи
 *
 * Подпись фиксирует **хеш содержимого**, а не идентификатор документа. Расхождение хеша —
 * не техническая деталь, а единственный способ увидеть, что строки правили после
 * подписания: бумага на экране тогда не та, которую подписывали. Поэтому
 * {@link fetchSignature} отдельным запросом есть, а не выводится из статуса.
 *
 * `matches === true` у **неподписанного** документа значит «противоречить нечему», а не
 * «всё в порядке»: смотреть надо на `signed`. Слить их в один флаг значило бы выставить
 * каждый черновик проверенным.
 *
 * ## Чего здесь нет — названо вслух
 *
 * - **Правки строк документа.** Её нет и на сервере: выпущенный документ заморожен, а
 *   исправление делается перевыпуском — процедура, которую ожидает закон. Обёртки под
 *   несуществующий эндпоинт здесь нет: функция без вызывающего выглядит рабочей
 *   возможностью, и через полгода на неё рассчитывают.
 * - **Файла документа и договора.** `pdfAttachmentId` и `attachmentId` в схеме есть,
 *   файлового хранилища в проекте нет (CLAUDE.md). Ссылки на скачивание не появилось.
 * - **Причины расторжения договора в ответе.** Колонки под неё в `crm.contract` нет:
 *   причина обязательна на входе и уходит в журнал приложения. На карточке её не видно —
 *   это ограничение, а не решение.
 */

const BASE = '/billing'

/** Строка первичного документа. Скидки в ней нет: цена уже пост-скидочная. */
export interface DocumentLine {
  id: string
  productId: string | null
  description: string
  uomId: string | null
  quantity: number
  unitPrice: number
  vatRate: number
  vatAmount: number
  lineTotal: number
  lineOrder: number
}

/** Первичный документ: УПД, акт, ТОРГ-12, счёт-фактура. */
export interface PrimaryDocument {
  id: string
  kind: string
  kindLabel: string
  number: string
  documentDate: string
  status: string
  organizationId: string
  counterpartyId: string
  invoiceId: string | null
  contractId: string | null
  shipmentId: string | null
  currency: string
  subtotal: number
  vatTotal: number
  total: number
  ediMessageId: string | null
  ediStatus: string | null
  ediSentAt: string | null
  signedAt: string | null
  signedBy: string | null
  /** Хеш содержимого на момент подписания; null у неподписанного. */
  signedContentHash: string | null
  /**
   * Сходится ли подпись с текущими строками.
   *
   * В сводке (без строк) сервер отдаёт `true` не потому, что проверил, а потому, что
   * проверять было нечем: строки не подняты. Судить о целостности можно только по
   * карточке, где строки есть.
   */
  signatureIntact: boolean
  reissuedFromId: string | null
  reissueReason: string | null
  valid: boolean
  lines: DocumentLine[] | null
}

/** Сверка подписи с текущим содержимым. */
export interface SignatureCheck {
  /** Подписан ли документ вообще. Именно на это надо смотреть первым. */
  signed: boolean
  signedHash: string | null
  currentHash: string
  /** У неподписанного всегда `true`: противоречить нечему. */
  matches: boolean
}

/** Договор с контрагентом. */
export interface Contract {
  id: string
  number: string
  counterpartyId: string
  organizationId: string
  contractType: string
  status: string
  currency: string
  signedOn: string | null
  validFrom: string | null
  validUntil: string | null
  amount: number | null
  paymentTermsDays: number
  /**
   * Можно ли сегодня оформить по нему заказ.
   *
   * Считается сервером по **датам**, а не берётся из статуса: договор, оставленный в
   * `active` после своей даты окончания, — оплошность ввода, и заказ по нему проскочить
   * не должен. Повторять эту формулу на клиенте значило бы завести второй ответ на
   * вопрос, по которому отказывают в заказе.
   */
  usableToday: boolean
}

// ------------------------------------------------------------------ документы: чтение

export function fetchDocument(id: string, signal?: AbortSignal): Promise<PrimaryDocument> {
  return request<PrimaryDocument>(`${BASE}/documents/${id}`, { signal })
}

/**
 * Сходится ли подпись с тем, что документ говорит сейчас.
 *
 * Отдельный запрос, а не поле документа: это **проверка**, а не свойство, и ответ на неё
 * зависит от текущего состояния строк. Спрятав её в документ, мы бы получили значение,
 * которое выглядит атрибутом и потому кешируется вместе с остальными.
 */
export function fetchSignature(id: string, signal?: AbortSignal): Promise<SignatureCheck> {
  return request<SignatureCheck>(`${BASE}/documents/${id}/signature`, { signal })
}

/**
 * Чем документ заменили.
 *
 * Цепочка, а не одна замена: перевыпустить можно и замену — она тоже бывает неверной, — и
 * цепочка отвечает на вопрос, какой из документов стоит в итоге.
 */
export function fetchReissues(id: string, signal?: AbortSignal): Promise<PrimaryDocument[]> {
  return request<PrimaryDocument[]>(`${BASE}/documents/${id}/reissues`, { signal })
}


// ------------------------------------------------------------------ документы: действия

/** Выпуск: с этого момента содержимое зафиксировано. */
export function issueDocument(id: string): Promise<PrimaryDocument> {
  return request<PrimaryDocument>(`${BASE}/documents/${id}/issue`, { method: 'POST' })
}

/**
 * Подписание.
 *
 * Право `approve`, а не `update`: подписание — обязательство, а не редактирование.
 * Подписант берёт сервер из текущей учётной записи; передавать его отсюда значило бы
 * дать подписать чужим именем.
 */
export function signDocument(id: string): Promise<PrimaryDocument> {
  return request<PrimaryDocument>(`${BASE}/documents/${id}/sign`, { method: 'POST' })
}

/** Отметка об отправке по ЭДО. Идентификатор обязателен — по нему потом ищут документ. */
export function markSentByEdi(id: string, messageId: string): Promise<PrimaryDocument> {
  return request<PrimaryDocument>(`${BASE}/documents/${id}/edi`, {
    method: 'POST',
    body: { messageId },
  })
}

/** Аннулирование. Подписанный документ домен не аннулирует — его перевыпускают. */
export function cancelDocument(id: string): Promise<PrimaryDocument> {
  return request<PrimaryDocument>(`${BASE}/documents/${id}/cancel`, { method: 'POST' })
}

/**
 * Перевыпуск с причиной.
 *
 * Причина обязательна и здесь заглушкой не подставляется: перевыпуск без неё неотличим от
 * постороннего документа с теми же строками, а аннулированный оригинал читается как чья-то
 * ошибка, а не как заменённая версия.
 */
export function reissueDocument(id: string, reason: string): Promise<PrimaryDocument> {
  return request<PrimaryDocument>(`${BASE}/documents/${id}/reissue`, {
    method: 'POST',
    body: { reason },
  })
}

// ------------------------------------------------------------------ договоры

/** Договоры контрагента, все статусы. */
export function fetchContracts(
  counterpartyId: string,
  signal?: AbortSignal,
): Promise<Contract[]> {
  return request<Contract[]>(`${BASE}/contracts?counterpartyId=${counterpartyId}`, { signal })
}

export function fetchContract(id: string, signal?: AbortSignal): Promise<Contract> {
  return request<Contract>(`${BASE}/contracts/${id}`, { signal })
}

/** Ввод в действие: дата подписания обязательна, срок окончания может быть открытым. */
export function activateContract(
  id: string,
  signedOn: string,
  validFrom: string | null,
  validUntil: string | null,
): Promise<Contract> {
  return request<Contract>(`${BASE}/contracts/${id}/activate`, {
    method: 'POST',
    body: { signedOn, validFrom, validUntil },
  })
}

/**
 * Продление до новой даты.
 *
 * Проверку «позже текущей» делает домен, и повторять её здесь не надо: своя проверка
 * завела бы второе место, где решается, что считать продлением, и разошлись бы они молча.
 */
export function prolongContract(id: string, validUntil: string): Promise<Contract> {
  return request<Contract>(`${BASE}/contracts/${id}/prolong`, {
    method: 'POST',
    body: { validUntil },
  })
}

/** Расторжение. Причина обязательна и заглушкой не подставляется. */
export function terminateContract(
  id: string,
  terminatedOn: string,
  reason: string,
): Promise<Contract> {
  return request<Contract>(`${BASE}/contracts/${id}/terminate`, {
    method: 'POST',
    body: { terminatedOn, reason },
  })
}

// ------------------------------------------------------------------ арифметика проверки

/**
 * Пересчёт строки по формуле формы.
 *
 * Существует ровно для одного — **проверить**, что показанное сходится. Повторив формулу
 * ради отображения, мы завели бы второй ответ на денежный вопрос. НДС начисляется на
 * сумму строки и округляется построчно: контрагент складывает напечатанные строки, и итог,
 * округлённый один раз в конце, с ними не сойдётся.
 */
export function lineArithmetic(line: {
  quantity: number
  unitPrice: number
  vatRate: number
}): { net: number; vat: number; total: number } {
  const net = round2(line.quantity * line.unitPrice)
  const vat = round2((net * line.vatRate) / 100)
  return { net, vat, total: round2(net + vat) }
}

/** Сходится ли итог документа с суммой его строк. */
export function linesReconcile(document: PrimaryDocument): boolean {
  const lines = document.lines ?? []
  if (lines.length === 0) return true
  const sum = lines.reduce((acc, line) => acc + line.lineTotal, 0)
  return round2(sum).toFixed(2) === document.total.toFixed(2)
}

function round2(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100
}
